"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";

import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";

export default function Rules({ data }: { data: PageAffiliateSchema }) {
  return (
    <section
      id="rules"
      className="w-full h-full lg:px-0 py-[5rem] px-[2rem] lg:py-[15rem]"
    >
      <div className="relative mx-auto w-full">
        <div className="relative mx-auto w-full z-[1] flex flex-col items-center">
          {data?.rulesChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif text={data.rulesChip} />
            </motion.div>
          )}
          <motion.div
            className="richtext richtext-center prose-p:text-[1.75rem] lg:prose-p:text-[2rem]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.rulesText && (
              <PortableText
                value={data?.rulesText}
                components={richTextComponent}
              />
            )}
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-[1rem] mt-[3rem] lg:mt-[4rem] w-[min(146.625rem,100%)]">
            {data?.rulesItems?.map((item, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.25,
                  delay: index * 0.08,
                  ease: [0.22, 0.61, 0.36, 1],
                }}
              >
                <div className="flex items-center h-full border gap-[2.75rem] rounded-[2rem] bg-white/90 p-[1rem] border-[#615B501A] lg:p-[2rem]">
                  <GroqImage
                    src={item.icon}
                    alt="icon"
                    className="w-[7.25rem] h-[7.25rem] min-w-[7.25rem] min-h-[7.25rem]"
                  />

                  <div className="">
                    <p className=" text-[#6F6F6F] text-[2rem] lg:text-[2rem] font-[500] leading-[140%] mb-0">
                      {item.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mx-auto mt-[4rem] w-fit lg:mt-[6rem]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <CTAButton data={data.rulesCta} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
